import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faEye,
  faEdit,
  faPlus,
  faCheck,
  faCheckCircle,
  faPencil,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ------------------ SEARCH FIELD ------------------ */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    className="form-control"
    placeholder="Search..."
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
  />
);

/* ------------------ TABLE COMPONENT ------------------ */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  setGlobalFilter,
  pageSize,
  setPageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter: tableSetFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize, globalFilter },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    tableSetFilter(globalFilter);
  }, [globalFilter]);

  const { pageIndex } = state;

  return (
    <>
      <table
        className="table table-striped"
        {...getTableProps()}
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  style={{
                    cursor: "pointer",
                    width: col.width,
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                  }}
                >
                  {col.render("Header")}{" "}
                  {col.isSorted ? (col.isSortedDesc ? "🔽" : "🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    style={{
                      color:
                        row.original.status === "Draft"
                          ? "#0d6efd"
                          : row.original.status === "Approved"
                          ? "#198754"
                          : "inherit",
                    }}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="d-flex align-items-center mb-3">
        <button
          className="btn btn-secondary me-auto"
          style={{ minWidth: 110 }}
          disabled={!canPreviousPage}
          onClick={previousPage}
        >
          Previous
        </button>

        <div className="flex-grow-1 text-center d-flex flex-column align-items-center">
          <div className="d-flex align-items-center gap-3">
            <strong>
              Page {pageIndex + 1} of {pageOptions.length}
            </strong>

            <select
              className="form-select form-select-sm w-auto"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[20, 30, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          className="btn btn-secondary ms-auto"
          style={{ minWidth: 110 }}
          disabled={!canNextPage}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* ------------------ MAIN COMPONENT ------------------ */
const OpeningStockList = ({ accessOfDepartments, permissions }) => {
  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState(null); // ✅ ADDED
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);

  const navigate = useNavigate();
  const code = localStorage.getItem("code");

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      let data = [];

      if (statusFilter === "pending") {
        const res = await apiClient.get(
          SERVER_BASE + "/getAllDraftOpeningStocks",
          { params: { code, accessOfDepartments } }
        );
        data = res.data;
      } else if (statusFilter === "approved") {
        const res = await apiClient.get(
          SERVER_BASE + "/getAllApprovedOpeningStocks",
          { params: { code, accessOfDepartments } }
        );
        data = res.data;
      } else {
        const [draftRes, approvedRes] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getAllDraftOpeningStocks", {
            params: { code, accessOfDepartments },
          }),
          apiClient.get(SERVER_BASE + "/getAllApprovedOpeningStocks", {
            params: { code, accessOfDepartments },
          }),
        ]);

        data = [...draftRes.data, ...approvedRes.data];
      }

      setTableData(data);
    } catch (err) {
      console.error("Error fetching opening stock:", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* ----------- DEPARTMENT FILTER LOGIC (ADDED) ----------- */
  const departmentOptions = useMemo(() => {
    const deps = [...new Set(tableData.map(d => d.department).filter(Boolean))];
    return [
      { value: "all", label: "All Departments" },
      ...deps.map(d => ({ value: d, label: d })),
    ];
  }, [tableData]);

  const filteredData = useMemo(() => {
    if (!departmentFilter || departmentFilter.value === "all") {
      return tableData;
    }
    return tableData.filter(
      d => d.department === departmentFilter.value
    );
  }, [tableData, departmentFilter]);

  /* ------------------ TABLE COLUMNS ------------------ */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: ({ value }) =>
          value ? moment(value).format("DD-MMM-YY") : "",
      },
      { Header: "Ref No.", accessor: "openingStockNo" },
      { Header: "Department", accessor: "department" },
      { Header: "Count", accessor: "itemCount" },
      {
        Header: "Items",
        accessor: "printNames",
        width: 400,
        minWidth: 400,
        maxWidth: 400,
      },
      { Header: "Status", accessor: "status" },
      {
        Header: "Preview",
        accessor: "Preview",
        Cell: ({ row }) => {
          const d = row.original;
          return (
            permissions?.read && (
              <button
                className="btn btn-sm btn-outline-success me-1"
                onClick={() =>
                  navigate("../../PreviewOpeningStock", {
                    state: { id: d.id },
                  })
                }
              >
                View
              </button>
            )
          );
        },
      },
      {
        Header: "#",
        Cell: ({ row }) => {
          const d = row.original;
          return (
            <div className="dropdown">
              <button
                className="btn btn-light btn-sm"
                data-bs-toggle="dropdown"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {d.status === "Draft" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() =>
                        navigate("../OpeningStock", { state: { rowData: d } })
                      }
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </li>
                )}

                <li><hr /></li>

                {d.createdBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} /> {d.createdBy}
                    {d.createdAt &&
                      ` (${moment(d.createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {d.updatedBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} /> {d.updatedBy}
                    {d.updatedAt &&
                      ` (${moment(d.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {d.approvedBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faCheckCircle} /> {d.approvedBy}
                    {d.approvedAt &&
                      ` (${moment(d.approvedAt).format(
                        "DD-MMM-YY hh:mm A"
                      )})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4 className="mb-3">Opening Stock</h4>

      <div className="row align-items-center mb-3">
        {permissions?.write && (
          <div className="col-md-2 mb-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../OpeningStock")}
            >
              Add
            </button>
          </div>
        )}

        <div className="col-md-5 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        {/* ✅ DEPARTMENT FILTER ADDED */}
        <div className="col-md-3 mb-2">
          <Select
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            placeholder="Department"
            isClearable
          />
        </div>

        <div className="col-md-2 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find(o => o.value === statusFilter)}
            onChange={(e) => setStatusFilter(e.value)}
          />
        </div>
      </div>

      {loadingData ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={filteredData}   
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default OpeningStockList;
